import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { X } from 'lucide-react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cn } from '@/lib/utils';

const capaFondo =
  'fixed inset-0 z-40 bg-[#141414]/50 backdrop-blur-[2px] data-[state=open]:animate-[entrar_150ms_ease-out]';

interface DialogoProps {
  abierto: boolean;
  onCambiarAbierto: (abierto: boolean) => void;
  titulo: string;
  descripcion?: string;
  children: ReactNode;
  pie?: ReactNode;
  ancho?: 'sm' | 'md' | 'lg' | 'xl';
  /** `false`: sin botón de cerrar; Escape y clic fuera no lo cierran (operaciones en curso). */
  cerrable?: boolean;
}

const anchos = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

export function Dialogo({ abierto, onCambiarAbierto, titulo, descripcion, children, pie, ancho = 'md', cerrable = true }: DialogoProps) {
  const bloquear = (e: Event) => {
    if (!cerrable) e.preventDefault();
  };
  return (
    <DialogPrimitive.Root open={abierto} onOpenChange={(v) => (cerrable || v) && onCambiarAbierto(v)}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={capaFondo} />
        <DialogPrimitive.Content
          onEscapeKeyDown={bloquear}
          onPointerDownOutside={bloquear}
          onInteractOutside={bloquear}
          className={cn(
            'fixed left-1/2 top-[8vh] z-50 flex max-h-[84vh] w-[calc(100vw-2rem)] -translate-x-1/2 flex-col rounded-xl border border-border bg-surface shadow-flotante animate-entrar',
            anchos[ancho],
          )}
        >
          <header className="flex items-start justify-between gap-4 border-b border-border px-5 pb-3 pt-4">
            <div>
              <DialogPrimitive.Title className="font-display text-xl">{titulo}</DialogPrimitive.Title>
              {descripcion ? (
                <DialogPrimitive.Description className="mt-0.5 text-[13px] text-foreground-muted">
                  {descripcion}
                </DialogPrimitive.Description>
              ) : (
                <DialogPrimitive.Description className="sr-only">{titulo}</DialogPrimitive.Description>
              )}
            </div>
            {cerrable && (
              <DialogPrimitive.Close className="rounded-md p-1.5 text-foreground-muted hover:bg-muted" aria-label="Cerrar">
                <X className="size-4" />
              </DialogPrimitive.Close>
            )}
          </header>
          <div className="overflow-y-auto px-5 py-4">{children}</div>
          {pie && <footer className="flex justify-end gap-2 border-t border-border px-5 py-3">{pie}</footer>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

interface PanelLateralProps extends Omit<DialogoProps, 'ancho'> {
  lado?: 'derecha' | 'izquierda';
}

/** Hoja lateral para detalles (ficha de cliente, detalle de cita…). */
export function PanelLateral({ abierto, onCambiarAbierto, titulo, descripcion, children, pie, lado = 'derecha' }: PanelLateralProps) {
  return (
    <DialogPrimitive.Root open={abierto} onOpenChange={onCambiarAbierto}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={capaFondo} />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-y-0 z-50 flex w-full max-w-md flex-col border-border bg-surface shadow-flotante animate-entrar',
            lado === 'derecha' ? 'right-0 border-l' : 'left-0 border-r',
          )}
        >
          <header className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
            <div>
              <DialogPrimitive.Title className="font-display text-xl">{titulo}</DialogPrimitive.Title>
              <DialogPrimitive.Description className={descripcion ? 'text-[13px] text-foreground-muted' : 'sr-only'}>
                {descripcion ?? titulo}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close className="rounded-md p-1.5 text-foreground-muted hover:bg-muted" aria-label="Cerrar">
              <X className="size-4" />
            </DialogPrimitive.Close>
          </header>
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {pie && <footer className="flex justify-end gap-2 border-t border-border px-5 py-3">{pie}</footer>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export const Menu = DropdownPrimitive.Root;
export const MenuDisparador = DropdownPrimitive.Trigger;

export function MenuContenido({ className, ...props }: ComponentPropsWithoutRef<typeof DropdownPrimitive.Content>) {
  return (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.Content
        sideOffset={6}
        className={cn(
          'z-50 min-w-48 rounded-lg border border-border bg-surface p-1 shadow-flotante animate-entrar',
          className,
        )}
        {...props}
      />
    </DropdownPrimitive.Portal>
  );
}

export function MenuItem({ className, ...props }: ComponentPropsWithoutRef<typeof DropdownPrimitive.Item>) {
  return (
    <DropdownPrimitive.Item
      className={cn(
        'flex cursor-pointer select-none items-center gap-2 rounded-md px-2.5 py-1.5 text-[13.5px] outline-none data-[highlighted]:bg-muted data-[disabled]:opacity-50 [&_svg]:size-4 [&_svg]:text-foreground-muted',
        className,
      )}
      {...props}
    />
  );
}

export function MenuSeparador() {
  return <DropdownPrimitive.Separator className="my-1 h-px bg-border" />;
}

export function MenuEtiqueta({ className, ...props }: ComponentPropsWithoutRef<typeof DropdownPrimitive.Label>) {
  return <DropdownPrimitive.Label className={cn('px-2.5 py-1.5 text-[12px] text-foreground-muted', className)} {...props} />;
}

export const Popover = PopoverPrimitive.Root;
export const PopoverDisparador = PopoverPrimitive.Trigger;

export function PopoverContenido({ className, ...props }: ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        sideOffset={6}
        className={cn('z-50 rounded-lg border border-border bg-surface p-3 shadow-flotante animate-entrar', className)}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

export function Ayuda({ texto, children }: { texto: string; children: ReactNode }) {
  return (
    <TooltipPrimitive.Provider delayDuration={300}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            sideOffset={6}
            className="z-50 rounded-md bg-primary px-2 py-1 text-[12px] text-on-primary shadow-flotante"
          >
            {texto}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
